import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from 'antd';
import {
    BoldOutlined,
    ItalicOutlined,
    UnderlineOutlined,
    StrikethroughOutlined,
    UnorderedListOutlined,
    OrderedListOutlined,
    LinkOutlined,
    SmileOutlined,
    PaperClipOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { marked } from 'marked';
import { sanitizeHtml } from '@utils/sanitizeHtml';

marked.setOptions({ gfm: true, breaks: true });

/** Plain clipboard from editors like VS Code often carries themed HTML — prefer MD source + convert. */
function looksLikeThemedEditorHtml(html) {
    if (!html || typeof html !== 'string') return false;
    return (
        /vscode|background(-color)?:\s*#?[0-9a-f]{3,8}/i.test(html) ||
        /color:\s*(#000|#fff|rgb\(\s*0\s*,\s*0\s*,\s*0\s*\)|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\))/i.test(html) ||
        /<(pre|code)[^>]*\sstyle=/i.test(html) ||
        /class="[^"]*(?:hljs|token|line-numbers)[^"]*"/i.test(html)
    );
}

function looksLikeMarkdown(text) {
    if (!text || typeof text !== 'string') return false;
    const s = text.trim();
    if (s.length < 2) return false;
    return (
        /^#{1,6}\s/m.test(s) ||
        /^ {0,3}[-*+]\s/m.test(s) ||
        /^ {0,3}\d+\.\s/m.test(s) ||
        /\*\*[^*\n]+\*\*/.test(s) ||
        /(^|\s)_[^_`\n]+_(\s|$)/.test(s) ||
        /`[^`\n]+`/.test(s) ||
        /```[\s\S]*```/.test(s) ||
        /\[.+\]\([^)\s]+\)/.test(s) ||
        /^ {0,3}>\s/m.test(s)
    );
}

function markdownToSanitizedHtml(md) {
    const raw = marked.parse(md, { async: false });
    return sanitizeHtml(typeof raw === 'string' ? raw : String(raw ?? ''));
}

/**
 * Preserve basic formatting on paste when it is expressed via inline styles or CSS classes
 * (common for Google Docs / MS Word clipboard HTML). We convert to semantic tags and then
 * rely on `sanitizeHtml` to drop unsafe attributes.
 *
 * Note: font size and font family are intentionally NOT preserved.
 */
function semanticizeFormattingFromHtml(html) {
    if (!html || typeof html !== 'string') return '';
    if (typeof document === 'undefined') return html;

    const canCompute = typeof window !== 'undefined' && typeof window.getComputedStyle === 'function';
    let probeHost = null;
    let probeRoot = null;

    try {
        // Root used to produce the final HTML output.
        const doc = document.implementation.createHTMLDocument('');
        const root = doc.createElement('div');
        root.innerHTML = html;

        // Probe tree attached to the real DOM so class-based styles can be computed.
        if (canCompute && document.body) {
            probeHost = document.createElement('div');
            probeHost.setAttribute('data-richtext-paste-probe', 'true');
            probeHost.style.position = 'fixed';
            probeHost.style.left = '-99999px';
            probeHost.style.top = '0';
            probeHost.style.width = '1px';
            probeHost.style.height = '1px';
            probeHost.style.overflow = 'hidden';
            probeHost.style.pointerEvents = 'none';
            probeHost.style.opacity = '0';
            document.body.appendChild(probeHost);

            probeRoot = document.createElement('div');
            probeRoot.innerHTML = html;
            probeHost.appendChild(probeRoot);
        }

        // Give matching spans a shared id so we can locate their probe counterpart.
        let nextId = 1;
        const tagSpans = (container) => {
            container.querySelectorAll('span').forEach((s) => {
                if (!s.getAttribute('data-paste-probe-id')) {
                    s.setAttribute('data-paste-probe-id', String(nextId++));
                }
            });
        };
        tagSpans(root);
        if (probeRoot) tagSpans(probeRoot);

        const getProbeEl = (el) => {
            if (!probeRoot) return null;
            const id = el.getAttribute('data-paste-probe-id');
            if (!id) return null;
            return probeRoot.querySelector(`span[data-paste-probe-id="${id}"]`);
        };

        const styleVal = (el, key) => {
            const inline = (el.style?.[key] || '').toString().trim();
            if (inline) return inline;
            const probeEl = getProbeEl(el);
            if (!probeEl || !canCompute) return '';
            const cs = window.getComputedStyle(probeEl);
            return (cs?.[key] || '').toString().trim();
        };

        const shouldBold = (el) => {
            const fw = styleVal(el, 'fontWeight').toLowerCase();
            if (!fw) return false;
            if (fw === 'bold' || fw === 'bolder') return true;
            const n = Number(fw);
            return Number.isFinite(n) && n >= 600;
        };
        const shouldItalic = (el) => styleVal(el, 'fontStyle').toLowerCase() === 'italic';
        const textDec = (el) => {
            const a = styleVal(el, 'textDecorationLine').toLowerCase();
            const b = styleVal(el, 'textDecoration').toLowerCase();
            return `${a} ${b}`.trim();
        };
        const shouldUnderline = (el) => textDec(el).includes('underline');
        const shouldStrike = (el) => textDec(el).includes('line-through');

        const wrap = (node, tag) => {
            const w = doc.createElement(tag);
            node.parentNode?.insertBefore(w, node);
            w.appendChild(node);
            return w;
        };

        const walk = (node) => {
            if (!node) return;
            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node;
                const tag = el.tagName?.toLowerCase();
                if (tag === 'span') {
                    const needsBold = shouldBold(el);
                    const needsItalic = shouldItalic(el);
                    const needsUnderline = shouldUnderline(el);
                    const needsStrike = shouldStrike(el);

                    // Drop styling hooks; semantic wrappers will preserve basic formatting.
                    if (el.hasAttribute('style')) el.removeAttribute('style');
                    if (el.hasAttribute('class')) el.removeAttribute('class');

                    let current = el;
                    if (needsStrike) current = wrap(current, 's');
                    if (needsUnderline) current = wrap(current, 'u');
                    if (needsItalic) current = wrap(current, 'em');
                    if (needsBold) current = wrap(current, 'strong');
                }
            }
            const children = node.childNodes ? Array.from(node.childNodes) : [];
            children.forEach(walk);
        };

        walk(root);
        return root.innerHTML;
    } catch {
        return html;
    } finally {
        if (probeHost && probeHost.parentNode) {
            probeHost.parentNode.removeChild(probeHost);
        }
    }
}

function escapeHtmlText(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function normalizeUrlForAnchor(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (/^https?:\/\//i.test(s)) return s;
    if (/^www\./i.test(s)) return `https://${s}`;
    return s;
}

/** Convert plain text to safe HTML with clickable links + <br/> line breaks. */
function linkifyPlainTextToHtml(text) {
    const s = String(text ?? '').replace(/\r\n/g, '\n');
    if (!s) return '';

    const urlRe = /\b(https?:\/\/[^\s<]+|www\.[^\s<]+)\b/gi;
    let out = '';
    let last = 0;
    let m;
    while ((m = urlRe.exec(s))) {
        const start = m.index;
        const end = start + m[0].length;
        const before = s.slice(last, start);
        out += escapeHtmlText(before).replace(/\n/g, '<br/>');

        const rawUrl = m[0];
        const href = normalizeUrlForAnchor(rawUrl);
        const label = escapeHtmlText(rawUrl);
        const safeHref = escapeHtmlText(href);
        out += `<a href="${safeHref}" contenteditable="false" style="font-weight:400;font-size:inherit;">${label}</a>`;
        last = end;
    }
    out += escapeHtmlText(s.slice(last)).replace(/\n/g, '<br/>');
    return out;
}

const EMOJI_LIST = [
    '👍',
    '👎',
    '❤️',
    '🔥',
    '⭐',
    '🎉',
    '👏',
    '🙏',
    '😊',
    '😂',
    '😅',
    '🤔',
    '😢',
    '✅',
    '❌',
    '💡',
    '🚀',
    '📌',
    '💪',
    '🙌',
];

const isDarkTheme = (theme) => theme?.colors?.background?.default === '#141414';

/** Light-only: submission modal — toolbar #E4E4E4, no seam with body */
const Toolbar = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px 8px;
    border-radius: 8px 8px 0 0;
    ${({ theme, $submissionNeutral }) => {
        const dark = isDarkTheme(theme);
        if ($submissionNeutral && !dark) {
            return `
                background: #e4e4e4;
                border: none;
            `;
        }
        return `
            border: 1px solid ${dark ? 'rgba(255, 255, 255, 0.14)' : '#d9d9d9'};
            border-bottom: none;
            background: ${dark ? 'transparent' : '#fafafa'};
        `;
    }}
`;
const primaryColor = ({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6';
/** tabIndex -1: Tab from previous field goes to the editor, not through every toolbar control */
const ToolbarBtn = styled.button.attrs({ tabIndex: -1 })`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background: ${({ $active, theme }) => ($active ? `${primaryColor({ theme })}` : 'transparent')};
    color: ${({ $active, theme }) =>
        $active ? '#fff' : theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#374151'};
    cursor: pointer;
    transition:
        background 0.15s,
        color 0.15s;
    &:hover {
        background: ${({ $active, theme }) =>
        $active
            ? `${primaryColor({ theme })}`
            : theme?.colors?.background?.default === '#141414'
                ? 'rgba(255,255,255,0.08)'
                : '#e5e7eb'};
        color: ${({ $active, theme }) =>
        $active ? '#fff' : theme?.colors?.background?.default === '#141414' ? '#fff' : '#1a1a1a'};
    }
    &:active {
        background: ${({ $active, theme }) =>
        $active
            ? `${primaryColor({ theme })}`
            : theme?.colors?.background?.default === '#141414'
                ? 'rgba(255,255,255,0.12)'
                : '#d1d5db'};
    }
`;
const EditorArea = styled.div`
    min-height: 120px;
    max-height: 240px;
    overflow-y: auto;
    padding: 10px 12px;
    border-radius: 0 0 8px 8px;
    font-size: 14px;
    line-height: 1.5;
    outline: none;
    word-break: break-word;
    ${({ theme, $submissionNeutral }) => {
        const dark = isDarkTheme(theme);
        if ($submissionNeutral && !dark) {
            return `
                border: none;
                background: #f6f6f6;
                color: #1a1a1a;
            `;
        }
        return `
            border: 1px solid ${dark ? 'rgba(255, 255, 255, 0.14)' : '#d9d9d9'};
            background: ${dark ? 'transparent' : '#fff'};
            color: ${dark ? 'rgba(255,255,255,0.92)' : '#1a1a1a'};
        `;
    }}

    &:empty::before {
        content: attr(data-placeholder);
        color: ${({ theme, $submissionNeutral }) => {
        const dark = isDarkTheme(theme);
        if ($submissionNeutral && !dark) return '#bfbfbf';
        return dark ? 'rgba(255,255,255,0.45)' : '#bfbfbf';
    }};
    }
    p {
        margin: 0 0 6px 0;
    }
    p:last-child {
        margin-bottom: 0;
    }
    ul,
    ol {
        margin: 0 0 6px 0;
        padding-left: 24px;
    }
    li {
        margin-bottom: 2px;
    }
    /* Link (URL) = blue; attachment (file/image) = purple; same as Link.jsx: no underline, underline on hover only */
    a {
        position: relative;
        font-size: inherit;
        font-weight: inherit;
        color: #1677ff;
        text-decoration: none;
        transition: color 0.25s ease;
    }
    a::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background: currentColor;
        transform: scaleX(0);
        transform-origin: left;
        transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    }
    a:hover::after {
        transform: scaleX(1);
    }
    a[href^="#attachment-"],
    a[data-attachment="true"] {
        color: #722ed1 !important;
    }
    a[data-link="true"]::before {
        content: '🔗 ';
    }
    a[data-attachment="true"][data-attachment-kind="image"]::before {
        content: '🖼️ ';
    }
    a[data-attachment="true"][data-attachment-kind="file"]::before {
        content: '📄 ';
    }
    a[href^="#attachment-"]:not([data-attachment-kind])::before {
        content: '📄 ';
    }
    a[href^="#mention-user-"],
    a[data-mention="user"],
    a[href^="#mention-milestone-"],
    a[data-mention="milestone"],
    a[href^="#mention-sprint-"],
    a[data-mention="sprint"] {
        color: ${({ theme }) => theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6'} !important;
    }
`;
const Wrapper = styled.div`
    width: 100%;
    position: relative;
    ${({ theme, $submissionNeutral }) => {
        const dark = isDarkTheme(theme);
        if ($submissionNeutral && !dark) {
            /* overflow: visible so the @-mention list (positioned below the editor) is not clipped */
            return `
                border: 1px solid #e4e4e4;
                border-radius: 8px;
                overflow: visible;
            `;
        }
        return '';
    }}
`;
/** Anchored to caret (fixed viewport coords); top/left set inline when open. */
const MentionDropdown = styled.div`
    position: fixed;
    width: 280px;
    max-width: calc(100vw - 16px);
    max-height: 220px;
    overflow-y: auto;
    border-radius: 8px;
    border: 1px solid ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.14)' : '#d9d9d9'};
    /* Floating list: solid surface (matches main app body token in Inside.jsx). */
    background: ${({ theme }) =>
        theme?.colors?.background?.default === '#141414' ? theme.colors.background.secondary : '#fff'};
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 2000;
`;
/** Section titles (Users / Milestones / Sprints) — distinct from selectable rows */
const MentionSectionHeader = styled.div`
    padding: 8px 12px 5px;
    margin-top: ${({ $first }) => ($first ? 0 : '6px')};
    border-bottom: 1px solid
        ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.12)' : '#e5e7eb')};
    margin-bottom: 2px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => (theme?.colors?.background?.default === '#141414' ? 'rgba(255, 255, 255, 0.5)' : '#71717a')};
    line-height: 1.25;
    user-select: none;
`;

const MentionItem = styled.button`
    display: block;
    width: 100%;
    text-align: left;
    border: none;
    background: ${({ $active, theme }) =>
        $active ? (theme?.colors?.primary || theme?.colors?.background?.primary || '#8b5cf6') : 'transparent'};
    color: ${({ $active, theme }) =>
        $active ? '#fff' : theme?.colors?.background?.default === '#141414' ? 'rgba(255,255,255,0.85)' : '#1a1a1a'};
    padding: 8px 12px;
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition:
        background 0.15s ease,
        color 0.15s ease;
    &:hover {
        background: ${({ $active, theme }) => {
        const p = primaryColor({ theme });
        if ($active) return p;
        return isDarkTheme(theme)
            ? `color-mix(in srgb, ${p} 24%, transparent)`
            : `color-mix(in srgb, ${p} 14%, transparent)`;
    }};
        color: ${({ $active, theme }) => ($active ? '#fff' : primaryColor({ theme }))};
    }
`;

function execCommandAndNotify(cmd, value = null) {
    document.execCommand(cmd, false, value);
}

function isSelectionIn(el) {
    if (typeof document === 'undefined' || !el) return false;
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    return el.contains(range.commonAncestorContainer);
}

/**
 * Rich text editor: Bold, Italic, Underline, Strikethrough, Lists, Link. Optional showEmojiPicker (e.g. for feedback).
 * value/onChange are HTML string (Ant Form compatible).
 */
function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

/** Show long file names as start....ext (e.g. ThemedAntTableWrap.....png). */
function truncateFileName(name, maxLen = 24) {
    if (!name || typeof name !== 'string' || name.length <= maxLen) return name;
    const extMatch = name.match(/\.[a-z0-9]+$/i);
    const ext = extMatch ? extMatch[0] : '';
    const base = ext ? name.slice(0, -ext.length) : name;
    const keep = maxLen - ext.length - 5;
    if (keep < 1) return name.slice(0, maxLen - 3) + '...';
    return base.slice(0, keep) + '.....' + ext;
}

function getAttachmentKindFromFile(file) {
    const type = String(file?.type || '').toLowerCase();
    if (type.startsWith('image/')) return 'image';
    return 'file';
}

function isFileLike(value) {
    return typeof File !== 'undefined' && value instanceof File;
}

function extractAttachmentIndexesFromHtml(html) {
    const input = String(html || '');
    if (!input) return [];
    const matches = input.match(/href="#attachment-(\d+)"/g) || [];
    const indexes = matches
        .map((token) => {
            const m = token.match(/#attachment-(\d+)/);
            return m ? Number(m[1]) : NaN;
        })
        .filter((n) => Number.isFinite(n));
    return Array.from(new Set(indexes)).sort((a, b) => a - b);
}

function isLikelyImageUrl(url) {
    const u = String(url || '').trim().toLowerCase();
    if (!u) return false;
    return /\.(png|jpe?g|gif|webp|bmp|svg|ico)(?:\?|#|$)/i.test(u);
}

const MENTION_LIMIT = 100;

/** Display label for mention user: name or email or id. */
function getMentionUserLabel(u) {
    if (u?.name && String(u.name).trim()) return String(u.name).trim();
    if (u?.email && String(u.email).trim()) return String(u.email).trim();
    return u?.id ? String(u.id) : '—';
}

/** One item in the @-mention dropdown: { type: 'user'|'milestone'|'sprint'|'tag', id, label, raw }. */
function getMentionItems(mentionUsers, mentionMilestones, mentionSprints, hashtagTags = []) {
    const items = [];
    (mentionUsers || []).slice(0, MENTION_LIMIT).forEach((u) => {
        items.push({ type: 'user', id: u.id, label: getMentionUserLabel(u), raw: u });
    });
    (mentionMilestones || []).forEach((m) => {
        items.push({ type: 'milestone', id: m.id, label: m.name || 'Milestone', raw: m });
    });
    (mentionSprints || []).forEach((s) => {
        items.push({ type: 'sprint', id: s.id, label: s.name || 'Sprint', raw: s });
    });
    (hashtagTags || []).forEach((t) => {
        items.push({ type: 'tag', id: t.id, label: t.tag, raw: t });
    });
    return items;
}

export function RichTextEditor({
    value = '',
    onChange,
    placeholder = 'Add description…',
    disabled,
    showEmojiPicker = false,
    showAttachButton = false,
    onAttachmentLinkClick,
    /** When provided, attached files are passed to parent instead of inserting data URLs into editor. */
    onFilesSelected,
    /** When provided, link toolbar opens parent's UI (e.g. modal); parent calls the callback with { url, title } to insert. */
    onLinkRequest,
    /** When user removes an attachment placeholder link (Backspace), called with the placeholder index so parent can remove the file. */
    onAttachmentRemoved,
    /** When user removes a non-mention non-attachment link, called with its href so parent can sync link lists. */
    onLinkRemoved,
    /** When using onFilesSelected, start placeholder indices here so multiple attach batches stay aligned with parent file list. */
    attachmentIndexOffset = 0,
    /** Users list for @-mention dropdown (e.g. project collaborators). Max MENTION_LIMIT. */
    mentionUsers = [],
    /** Milestones for @-mention (e.g. from useProjectMilestones). */
    mentionMilestones = [],
    /** Sprints for @-mention (e.g. from useProjectSprints). */
    mentionSprints = [],
    /** Public tags for #-hashtag (e.g. from usePublicTags). */
    hashtagTags = [],
    /** When false, hide file attach and ignore attach handlers. */
    isAttachment = true,
    /** When false, hide link tool and block link insertion (including modal flow). */
    isLink = true,
    /** When false, disable @-mention dropdown and insertion. */
    isMention = true,
    /**
     * Light mode: submission-style surfaces — toolbar #E4E4E4, editor #F6F6F6, single outer border, no toolbar/body seam.
     * Dark mode: unchanged (ignores this flag).
     */
    submissionNeutralLight = false,
    ...rest
}) {
    const elRef = useRef(null);
    const fileInputRef = useRef(null);
    const savedRangeRef = useRef(null);
    const mentionDropdownRef = useRef(null);
    const mentionRangeRef = useRef(null);
    const attachmentIndexesRef = useRef([]);
    /** True until user presses ArrowUp/ArrowDown; then highlight row shows primary selection. */
    const mentionFirstKeyRef = useRef(true);
    const [mentionOpen, setMentionOpen] = useState(false);
    const [mentionTrigger, setMentionTrigger] = useState('@');
    const [mentionQuery, setMentionQuery] = useState('');
    const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
    const [mentionKeyboardNav, setMentionKeyboardNav] = useState(false);
    /** Viewport coords for {@link MentionDropdown} (below caret, not below whole editor). */
    const [mentionPosition, setMentionPosition] = useState(null);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [formatState, setFormatState] = useState({
        bold: false,
        italic: false,
        underline: false,
        strikeThrough: false,
        insertUnorderedList: false,
        insertOrderedList: false,
    });

    const closeMentionDropdown = useCallback(() => {
        setMentionOpen(false);
        setMentionPosition(null);
        setMentionKeyboardNav(false);
        mentionFirstKeyRef.current = true;
    }, []);

    const placeMentionDropdown = useCallback((range) => {
        if (!range || typeof window === 'undefined') return;
        const r = range.cloneRange();
        r.collapse(false);
        let br = r.getBoundingClientRect();
        if (br.width === 0 && br.height === 0) {
            const rects = r.getClientRects();
            if (rects.length > 0) {
                br = rects[rects.length - 1];
            }
        }
        const dropdownMaxH = 220;
        const dropdownW = 280;
        let top = br.bottom + 2;
        let left = br.left;
        if (top + dropdownMaxH > window.innerHeight - 8) {
            top = Math.max(8, br.top - dropdownMaxH - 2);
        }
        left = Math.max(8, Math.min(left, window.innerWidth - dropdownW - 8));
        setMentionPosition({ top, left });
    }, []);

    /** Get plain text from start of container up to cursor (range start). */
    const getTextBeforeCursor = useCallback((container, range) => {
        try {
            const r = range.cloneRange();
            r.setStart(container, 0);
            r.setEnd(range.startContainer, range.startOffset);
            return r.toString();
        } catch {
            return '';
        }
    }, []);

    const syncFromValue = useCallback(() => {
        if (!elRef.current) return;
        const next = value || '';
        if (elRef.current.innerHTML !== next) {
            elRef.current.innerHTML = next;
        }
        attachmentIndexesRef.current = extractAttachmentIndexesFromHtml(next);
    }, [value]);

    useEffect(() => {
        syncFromValue();
    }, [syncFromValue]);

    const updateFormatState = useCallback(() => {
        const el = elRef.current;
        if (typeof document === 'undefined' || !el || !isSelectionIn(el)) return;
        setFormatState({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            underline: document.queryCommandState('underline'),
            strikeThrough: document.queryCommandState('strikeThrough'),
            insertUnorderedList: document.queryCommandState('insertUnorderedList'),
            insertOrderedList: document.queryCommandState('insertOrderedList'),
        });
    }, []);

    useEffect(() => {
        const doc = typeof document !== 'undefined' ? document : null;
        if (!doc) return;
        const onSelectionChange = () => {
            if (elRef.current && isSelectionIn(elRef.current)) {
                updateFormatState();

                if (mentionOpen) {
                    const sel = doc.getSelection();
                    if (sel && sel.rangeCount > 0) {
                        const range = sel.getRangeAt(0);
                        const textBefore = getTextBeforeCursor(elRef.current, range);
                        const match = textBefore.match(/([@#])([^\s]*)$/);
                        if (!match) {
                            closeMentionDropdown();
                        } else {
                            setMentionTrigger(match[1]);
                            setMentionQuery(match[2] || '');
                            placeMentionDropdown(range);
                        }
                    }
                }
            }
        };
        doc.addEventListener('selectionchange', onSelectionChange);
        return () => doc.removeEventListener('selectionchange', onSelectionChange);
    }, [updateFormatState, mentionOpen, closeMentionDropdown, getTextBeforeCursor, placeMentionDropdown]);

    useEffect(() => {
        if (!mentionOpen) return;
        const handleMousedown = (e) => {
            const target = e.target;
            if (mentionDropdownRef.current && mentionDropdownRef.current.contains(target)) return;
            if (elRef.current && elRef.current.contains(target)) return;
            closeMentionDropdown();
        };
        document.addEventListener('mousedown', handleMousedown, { capture: true });
        document.addEventListener('touchstart', handleMousedown, { capture: true });
        return () => {
            document.removeEventListener('mousedown', handleMousedown, { capture: true });
            document.removeEventListener('touchstart', handleMousedown, { capture: true });
        };
    }, [mentionOpen, closeMentionDropdown]);

    const handleInput = useCallback(() => {
        const el = elRef.current;
        if (!el || !onChange) return;
        const html = el.innerHTML;
        const prevIndexes = attachmentIndexesRef.current;
        const nextIndexes = extractAttachmentIndexesFromHtml(html);
        attachmentIndexesRef.current = nextIndexes;
        if (typeof onAttachmentRemoved === 'function' && prevIndexes.length > nextIndexes.length) {
            const nextSet = new Set(nextIndexes);
            const removed = prevIndexes.filter((idx) => !nextSet.has(idx)).sort((a, b) => b - a);
            removed.forEach((idx) => onAttachmentRemoved(idx));
        }
        onChange(html === '<br>' ? '' : html);

        if (!isMention) {
            closeMentionDropdown();
            return;
        }

        // @-mention: detect "@" or "#" and text after it before cursor
        const mentionList = getMentionItems(mentionUsers, mentionMilestones, mentionSprints, hashtagTags);
        if (mentionList.length === 0) {
            closeMentionDropdown();
            return;
        }
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (!el.contains(range.startContainer)) return;
        const textBefore = getTextBeforeCursor(el, range);
        const match = textBefore.match(/([@#])([^\s]*)$/);
        if (match) {
            try {
                mentionRangeRef.current = range.cloneRange();
            } catch (_) {
                mentionRangeRef.current = null;
            }
            setMentionTrigger(match[1]);
            setMentionQuery(match[2] || '');
            placeMentionDropdown(range);
            setMentionOpen(true);
            setMentionKeyboardNav(false);
            mentionFirstKeyRef.current = true;
            setMentionHighlightIndex(0);
        } else {
            closeMentionDropdown();
            mentionRangeRef.current = null;
        }
    }, [
        onChange,
        onAttachmentRemoved,
        isMention,
        closeMentionDropdown,
        placeMentionDropdown,
        mentionUsers,
        mentionMilestones,
        mentionSprints,
    ]);

    const refreshMentionPosition = useCallback(() => {
        const el = elRef.current;
        if (!el || !mentionOpen) return;
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        if (!el.contains(range.startContainer)) return;
        const textBefore = getTextBeforeCursor(el, range);
        const match = textBefore.match(/([@#])([^\s]*)$/);
        if (!match) return;
        placeMentionDropdown(range);
    }, [mentionOpen, getTextBeforeCursor, placeMentionDropdown]);

    useEffect(() => {
        if (!mentionOpen) return;
        const update = () => {
            refreshMentionPosition();
        };
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        const el = elRef.current;
        el?.addEventListener('scroll', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
            el?.removeEventListener('scroll', update);
        };
    }, [mentionOpen, refreshMentionPosition]);

    /**
     * Set the start of range to the position at character index charIndex from the start of container.
     * Range end is unchanged. Used so we delete exactly "@query" and never the space before it.
     */
    const setRangeStartToCharIndex = useCallback((container, range, charIndex) => {
        let count = 0;
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null, false);
        let node;
        while ((node = walker.nextNode())) {
            const len = node.textContent?.length ?? 0;
            if (count + len > charIndex || (charIndex === 0 && len > 0)) {
                try {
                    range.setStart(node, charIndex <= 0 ? 0 : Math.min(charIndex - count, len));
                } catch (_) { }
                return;
            }
            count += len;
            if (node === range.endContainer) break;
        }
    }, []);

    const saveSelection = useCallback(() => {
        const sel = document.getSelection();
        const el = elRef.current;
        if (!sel || sel.rangeCount === 0 || !el || !isSelectionIn(el)) return;
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }, []);

    const restoreSelectionAndInsert = useCallback(
        (html) => {
            const el = elRef.current;
            if (!el || !onChange) return;
            el.focus();
            const sel = document.getSelection();
            if (savedRangeRef.current && sel) {
                sel.removeAllRanges();
                sel.addRange(savedRangeRef.current);
                savedRangeRef.current = null;
            }
            document.execCommand('insertHTML', false, html);
            handleInput();

            // Force focus and caret position to ensure user can continue typing without clicking.
            requestAnimationFrame(() => {
                if (elRef.current) {
                    elRef.current.focus();
                    const s = document.getSelection();
                    if (s && s.rangeCount > 0) {
                        // Move caret to the very end of the inserted HTML (after the space/zero-width space).
                        s.collapseToEnd();
                    }
                }
            });
        },
        [handleInput, onChange]
    );

    const mentionItemsAll = useMemo(
        () => getMentionItems(mentionUsers, mentionMilestones, mentionSprints, hashtagTags),
        [mentionUsers, mentionMilestones, mentionSprints, hashtagTags],
    );
    const filteredMentionItems = useMemo(() => {
        if (!isMention || !mentionOpen || mentionItemsAll.length === 0) return [];
        const q = (mentionQuery || '').trim().toLowerCase();
        if (mentionTrigger === '#') {
            return mentionItemsAll.filter((it) => it.type === 'tag' && String(it.label || '').toLowerCase().includes(q));
        }
        if (!q) return mentionItemsAll.filter((it) => it.type !== 'tag');
        return mentionItemsAll.filter((item) => {
            if (item.type === 'tag') return false;
            const label = (item.label || '').toLowerCase();
            if (item.type === 'user' && item.raw?.email) {
                return label.includes(q) || (item.raw.email || '').toLowerCase().includes(q);
            }
            return label.includes(q);
        });
    }, [isMention, mentionItemsAll, mentionOpen, mentionQuery, mentionTrigger]);

    const insertMention = useCallback(
        (user) => {
            if (!isMention) return;
            const el = elRef.current;
            if (!el || !onChange) return;
            const sel = document.getSelection();
            if (!sel) return;
            el.focus();
            const savedRange = mentionRangeRef.current;
            let toRemove = 0;
            let textBefore = '';
            if (savedRange && el.contains(savedRange.startContainer)) {
                textBefore = getTextBeforeCursor(el, savedRange);
                const match = textBefore.match(/([@#])([^\s]*)$/);
                if (match) toRemove = match[0].length;
            }
            if (toRemove > 0 && savedRange && el.contains(savedRange.startContainer) && textBefore.length >= toRemove) {
                let startCharIndex = textBefore.length - toRemove;
                while (startCharIndex < textBefore.length && /\s/.test(textBefore[startCharIndex])) {
                    startCharIndex++;
                }
                if (startCharIndex < textBefore.length) {
                    const deleteRange = savedRange.cloneRange();
                    setRangeStartToCharIndex(el, deleteRange, startCharIndex);
                    if (deleteRange.toString().length > 0) {
                        deleteRange.deleteContents();
                        sel.removeAllRanges();
                        sel.addRange(deleteRange);
                    }
                }
            }
            mentionRangeRef.current = null;
            const item = user;
            const type = item?.type ?? 'user';
            const id = (item?.id ?? '').replace(/"/g, '&quot;');
            const label = item?.label ?? (type === 'user' ? getMentionUserLabel(item?.raw) : '');
            const safeLabel = String(label).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
            const displayText = type === 'tag' ? safeLabel : (type === 'user' ? `@${safeLabel}` : safeLabel);
            const href = type === 'tag'
                ? `hashtag://tag?tag=${encodeURIComponent(label)}`
                : type === 'user'
                    ? `#mention-user-${id}`
                    : type === 'milestone'
                        ? `#mention-milestone-${id}`
                        : `#mention-sprint-${id}`;
            const dataAttrs = type === 'tag'
                ? ` data-hashtag="${safeLabel}"`
                : type === 'user'
                    ? ` data-mention="user" data-user-id="${id}" data-username="${safeLabel}"`
                    : type === 'milestone'
                        ? ` data-mention="milestone" data-milestone-id="${id}" data-name="${safeLabel}"`
                        : ` data-mention="sprint" data-sprint-id="${id}" data-name="${safeLabel}"`;
            const mentionHtml = `<a href="${href}"${dataAttrs} contenteditable="false" style="font-weight:400;font-size:inherit;">${displayText}</a>`;
            const textLeft = textBefore.slice(0, textBefore.length - toRemove);
            /**
             * Ensure space on both sides.
             * Even if at the start of line, we add a space if the user requested "left and right".
             */
            const needsSpaceBefore = !textLeft || !/\s$/.test(textLeft);
            const prefix = needsSpaceBefore ? ' ' : '';

            saveSelection();
            /** Spacing: prefix space (if needed) + mention link + mandatory space after + zero-width space for caret escape. */
            restoreSelectionAndInsert(prefix + mentionHtml + ' \u200B');
            closeMentionDropdown();
        },
        [isMention, onChange, closeMentionDropdown, restoreSelectionAndInsert, getTextBeforeCursor, saveSelection, setRangeStartToCharIndex]
    );

    const handleToolbar = useCallback(
        (cmd, valueArg = null) =>
            (e) => {
                e.preventDefault();
                elRef.current?.focus();
                execCommandAndNotify(cmd, valueArg);
                handleInput();
                setTimeout(updateFormatState, 0);
            },
        [handleInput, updateFormatState],
    );

    const handleLink = useCallback(
        (e) => {
            e.preventDefault();
            if (!isLink) return;
            elRef.current?.focus();
            saveSelection();
            if (onLinkRequest) {
                onLinkRequest(({ url, title }) => {
                    if (!url) return;
                    if (!elRef.current || !onChange) return;
                    const text = (title || url).replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
                    const href = url.replace(/"/g, '&quot;');
                    const isImage = isLikelyImageUrl(href);
                    const attrs = isImage
                        ? 'data-attachment="true" data-attachment-kind="image"'
                        : 'data-link="true"';
                    const linkHtml = `<a href="${href}" ${attrs} contenteditable="false" style="font-weight:400;font-size:inherit;">${text}</a>`;
                    /** Same spacing as file attachments: room before/after + caret after (\\u200B). */
                    restoreSelectionAndInsert(` ${linkHtml} \u200B`);
                });
                return;
            }
            const url = window.prompt('Enter URL:');
            if (url) {
                execCommandAndNotify('createLink', url);
                handleInput();
            }
        },
        [handleInput, isLink, onLinkRequest, onChange, saveSelection, restoreSelectionAndInsert],
    );

    const insertEmoji = useCallback(
        (emoji) => {
            elRef.current?.focus();
            document.execCommand('insertText', false, emoji);
            handleInput();
        },
        [handleInput]
    );

    const handleAttachClick = useCallback(() => {
        if (!isAttachment) return;
        const el = elRef.current;
        if (!el) return;
        el.focus();
        saveSelection();
        fileInputRef.current?.click();
    }, [isAttachment, saveSelection]);

    const handleAttachFileChange = useCallback(
        (e) => {
            if (!isAttachment) {
                e.target.value = '';
                return;
            }
            const files = e.target?.files;
            if (!files?.length) return;
            const fileList = Array.from(files);
            if (onFilesSelected) {
                const base = typeof attachmentIndexOffset === 'number' ? attachmentIndexOffset : 0;
                const parts = fileList.map((file, i) => {
                    const idx = base + i;
                    const name = file.name || 'attachment';
                    const kind = getAttachmentKindFromFile(file);
                    const displayName = truncateFileName(name);
                    const safeDisplay = displayName.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const safeFullName = name.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    return `<a href="#attachment-${idx}" data-attachment="true" data-attachment-kind="${kind}" data-attachment-index="${idx}" data-attachment-fullname="${safeFullName}" contenteditable="false" style="font-weight:400;font-size:inherit;">${safeDisplay}</a>`;
                });
                if (parts.length) {
                    if (!elRef.current || !onChange) {
                        e.target.value = '';
                        return;
                    }
                    /** Insert into markdown first, then notify parent so pending files stay 1:1 with #attachment placeholders. */
                    const html = ` ${parts.join(' ')} \u200B`;
                    restoreSelectionAndInsert(html);
                    onFilesSelected(fileList);
                }
                e.target.value = '';
                return;
            }
            const process = async () => {
                const parts = [];
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const name = file.name || 'attachment';
                    try {
                        const dataUrl = await readFileAsDataUrl(file);
                        const safeName = name.replace(/"/g, '&quot;');
                        const kind = getAttachmentKindFromFile(file);
                        const linkHtml = `<a href="${dataUrl}" data-attachment="true" data-attachment-kind="${kind}" contenteditable="false" style="font-weight:400;font-size:inherit;">${safeName}</a>`;
                        parts.push(linkHtml);
                    } catch (_) {
                        parts.push(` ${name} `);
                    }
                }
                if (parts.length) restoreSelectionAndInsert(parts.join(' ') + ' ');
            };
            process();
            e.target.value = '';
        },
        [isAttachment, onFilesSelected, onChange, restoreSelectionAndInsert, attachmentIndexOffset]
    );

    const insertFilesAsAttachments = useCallback(
        (fileList) => {
            const files = Array.from(fileList || []).filter(isFileLike);
            if (!files.length || !isAttachment) return false;
            if (onFilesSelected) {
                const base = typeof attachmentIndexOffset === 'number' ? attachmentIndexOffset : 0;
                const parts = files.map((file, i) => {
                    const idx = base + i;
                    const name = file.name || 'attachment';
                    const kind = getAttachmentKindFromFile(file);
                    const displayName = truncateFileName(name);
                    const safeDisplay = displayName.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    const safeFullName = name.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                    return `<a href="#attachment-${idx}" data-attachment="true" data-attachment-kind="${kind}" data-attachment-index="${idx}" data-attachment-fullname="${safeFullName}" contenteditable="false" style="font-weight:400;font-size:inherit;">${safeDisplay}</a>`;
                });
                if (!parts.length || !elRef.current || !onChange) return false;
                const html = ` ${parts.join(' ')} \u200B`;
                restoreSelectionAndInsert(html);
                onFilesSelected(files);
                return true;
            }

            const process = async () => {
                const parts = [];
                for (let i = 0; i < files.length; i++) {
                    const file = files[i];
                    const name = file.name || 'attachment';
                    try {
                        const dataUrl = await readFileAsDataUrl(file);
                        const safeName = name.replace(/"/g, '&quot;');
                        const kind = getAttachmentKindFromFile(file);
                        const linkHtml = `<a href="${dataUrl}" data-attachment="true" data-attachment-kind="${kind}" contenteditable="false" style="font-weight:400;font-size:inherit;">${safeName}</a>`;
                        parts.push(linkHtml);
                    } catch (_) {
                        parts.push(` ${name} `);
                    }
                }
                if (parts.length) restoreSelectionAndInsert(parts.join(' ') + ' ');
            };
            process();
            return true;
        },
        [attachmentIndexOffset, isAttachment, onFilesSelected, onChange, restoreSelectionAndInsert],
    );

    const handlePaste = useCallback((e) => {
        const clipboard = e.clipboardData;
        const clipboardFiles = Array.from(clipboard?.items || [])
            .map((item) => {
                try {
                    return item?.kind === 'file' ? item.getAsFile() : null;
                } catch {
                    return null;
                }
            })
            .filter(isFileLike);
        if (clipboardFiles.length > 0) {
            e.preventDefault();
            insertFilesAsAttachments(clipboardFiles);
            return;
        }

        e.preventDefault();
        const html = clipboard?.getData('text/html') || '';
        const text = clipboard?.getData('text/plain') || '';

        // Markdown source (e.g. .md in VS Code): convert to HTML; ignore themed clipboard HTML.
        if (text && looksLikeMarkdown(text)) {
            const safe = markdownToSanitizedHtml(text);
            document.execCommand('insertHTML', false, safe);
            handleInput();
            return;
        }

        // Themed editor HTML (VS Code etc.): strip inline styles — no black background / syntax colors.
        if (html && (/<[^>]+>/.test(html) || looksLikeThemedEditorHtml(html))) {
            const semantic = semanticizeFormattingFromHtml(html);
            const safe = sanitizeHtml(semantic);
            document.execCommand('insertHTML', false, safe);
            handleInput();
            return;
        }

        // Plain text: preserve line breaks.
        if (text) {
            const htmlText = linkifyPlainTextToHtml(text);
            document.execCommand('insertHTML', false, htmlText);
        } else {
            document.execCommand('insertText', false, '');
        }
        handleInput();
    }, [handleInput, insertFilesAsAttachments]);

    const handleDragOver = useCallback(
        (e) => {
            const hasFiles = Array.from(e?.dataTransfer?.types || []).includes('Files');
            if (!hasFiles || !isAttachment || disabled) return;
            e.preventDefault();
            setIsDraggingOver(true);
        },
        [isAttachment, disabled],
    );

    const handleDragLeave = useCallback(() => {
        setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback(
        (e) => {
            const files = Array.from(e?.dataTransfer?.files || []).filter(isFileLike);
            setIsDraggingOver(false);
            if (!files.length || !isAttachment || disabled) return;
            e.preventDefault();
            elRef.current?.focus();
            saveSelection();
            insertFilesAsAttachments(files);
        },
        [disabled, insertFilesAsAttachments, isAttachment, saveSelection],
    );

    const moveSelectionOutOfLink = useCallback((el, linkEl) => {
        if (typeof document === 'undefined' || !el?.contains(linkEl)) return;
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        const isInside = linkEl.contains(range.startContainer) || linkEl.contains(range.endContainer);
        if (!isInside) return;
        const r = document.createRange();
        r.setStartAfter(linkEl);
        r.setEndAfter(linkEl);
        sel.removeAllRanges();
        sel.addRange(r);
    }, []);

    const handleEditorClick = useCallback(
        (e) => {
            updateFormatState();
            const target = e.target;
            if (!target || !(target instanceof HTMLElement)) return;
            const linkEl = target.closest('a');
            if (!linkEl) return;
            const href = linkEl.getAttribute('href') || '';
            e.preventDefault();
            if (href.startsWith('#attachment-')) {
                const fullName = linkEl.getAttribute('data-attachment-fullname') || linkEl.textContent || 'attachment';
                if (onAttachmentLinkClick) onAttachmentLinkClick(href, fullName);
                requestAnimationFrame(() => moveSelectionOutOfLink(elRef.current, linkEl));
                return;
            }
            if (linkEl.hasAttribute('data-attachment')) {
                const fullName = linkEl.getAttribute('data-attachment-fullname') || linkEl.textContent || 'attachment';
                if (onAttachmentLinkClick) onAttachmentLinkClick(href, fullName);
                requestAnimationFrame(() => moveSelectionOutOfLink(elRef.current, linkEl));
                return;
            }
            if (href.startsWith('#mention-user-') || href.startsWith('#mention-milestone-') || href.startsWith('#mention-sprint-')) {
                requestAnimationFrame(() => moveSelectionOutOfLink(elRef.current, linkEl));
                return;
            }
            if (href.startsWith('http://') || href.startsWith('https://')) {
                window.open(href, '_blank', 'noopener,noreferrer');
            }
            requestAnimationFrame(() => moveSelectionOutOfLink(elRef.current, linkEl));
        },
        [onAttachmentLinkClick, updateFormatState, moveSelectionOutOfLink]
    );

    const getLinkToSkip = (node) => {
        if (!node || !elRef.current?.contains(node)) return null;
        const a = node.nodeType === Node.ELEMENT_NODE && node.tagName === 'A'
            ? node
            : node.parentElement?.closest?.('a');
        return a && elRef.current.contains(a) ? a : null;
    };

    const getNextNode = (container, offset) => {
        if (container.nodeType === Node.TEXT_NODE) {
            if (offset < (container.textContent?.length ?? 0)) return null;
            return container.nextSibling ?? container.parentElement?.nextSibling ?? null;
        }
        if (container.nodeType === Node.ELEMENT_NODE) {
            const len = container.childNodes?.length ?? 0;
            if (offset < len) return container.childNodes[offset];
            return container.nextSibling ?? null;
        }
        return null;
    };

    const getPreviousNode = (container, offset) => {
        if (container.nodeType === Node.TEXT_NODE) {
            if (offset > 0) return null;
            return container.previousSibling ?? container.parentElement?.previousSibling ?? null;
        }
        if (container.nodeType === Node.ELEMENT_NODE) {
            if (offset > 0) return container.childNodes[offset - 1];
            return container.previousSibling ?? null;
        }
        return null;
    };

    const handleEditorKeyDown = useCallback(
        (e) => {
            const el = elRef.current;
            if (!el || typeof document === 'undefined') return;
            const sel = document.getSelection();
            if (!sel || sel.rangeCount === 0) return;
            const range = sel.getRangeAt(0);
            const key = e.key;

            // Mention dropdown open: handle navigation and selection
            if (mentionOpen && filteredMentionItems.length > 0) {
                if (key === 'Escape') {
                    e.preventDefault();
                    closeMentionDropdown();
                    return;
                }
                if (key === 'Enter') {
                    e.preventDefault();
                    const item = filteredMentionItems[mentionHighlightIndex];
                    if (item) insertMention(item);
                    return;
                }
                if (key === 'ArrowDown') {
                    e.preventDefault();
                    const len = filteredMentionItems.length;
                    if (mentionFirstKeyRef.current) {
                        mentionFirstKeyRef.current = false;
                        setMentionKeyboardNav(true);
                        setMentionHighlightIndex(0);
                    } else {
                        setMentionHighlightIndex((i) => (i + 1) % len);
                    }
                    return;
                }
                if (key === 'ArrowUp') {
                    e.preventDefault();
                    const len = filteredMentionItems.length;
                    if (mentionFirstKeyRef.current) {
                        mentionFirstKeyRef.current = false;
                        setMentionKeyboardNav(true);
                        setMentionHighlightIndex(len > 0 ? len - 1 : 0);
                    } else {
                        setMentionHighlightIndex((i) => (i - 1 + len) % len);
                    }
                    return;
                }
            }

            if (!sel.isCollapsed) return;

            const nodeInLink = (n) => {
                if (!n || !el.contains(n)) return null;
                const a = n.nodeType === Node.ELEMENT_NODE && n.tagName === 'A' ? n : n.parentElement?.closest?.('a');
                return a && el.contains(a) ? a : null;
            };

            const linkContainingCursor = nodeInLink(range.startContainer) || (range.startContainer.nodeType === Node.ELEMENT_NODE && range.startContainer.tagName === 'A' ? range.startContainer : null);

            if (linkContainingCursor) {
                e.preventDefault();
                const href = linkContainingCursor.getAttribute('href') || '';
                const isMentionLink = href.startsWith('#mention-user-') || href.startsWith('#mention-milestone-') || href.startsWith('#mention-sprint-');
                if (key === 'ArrowRight') {
                    const r = document.createRange();
                    r.setStartAfter(linkContainingCursor);
                    r.setEndAfter(linkContainingCursor);
                    sel.removeAllRanges();
                    sel.addRange(r);
                } else if (key === 'ArrowLeft') {
                    const r = document.createRange();
                    r.setStartBefore(linkContainingCursor);
                    r.setEndBefore(linkContainingCursor);
                    sel.removeAllRanges();
                    sel.addRange(r);
                } else if (key === 'Backspace') {
                    const linkEl = linkContainingCursor;
                    const linkHref = linkEl.getAttribute('href') || '';
                    const attachmentMatch = linkHref.match(/^#attachment-(\d+)$/);
                    const removedAttachmentIndex = linkEl.hasAttribute('data-attachment') && attachmentMatch ? parseInt(attachmentMatch[1], 10) : -1;
                    if (removedAttachmentIndex >= 0 && onAttachmentRemoved) onAttachmentRemoved(removedAttachmentIndex);
                    if (removedAttachmentIndex < 0 && !isMentionLink && typeof onLinkRemoved === 'function' && linkHref && !linkHref.startsWith('#')) {
                        onLinkRemoved(linkHref);
                    }
                    const parent = linkEl.parentNode;
                    const idx = parent ? Array.from(parent.childNodes).indexOf(linkEl) : 0;
                    if (parent) linkEl.remove();
                    if (removedAttachmentIndex >= 0 && el) {
                        el.querySelectorAll('a[href^="#attachment-"]').forEach((a) => {
                            const m = (a.getAttribute('href') || '').match(/^#attachment-(\d+)$/);
                            if (m) {
                                const n = parseInt(m[1], 10);
                                if (n > removedAttachmentIndex) a.setAttribute('href', `#attachment-${n - 1}`);
                            }
                        });
                    }
                    if (parent) {
                        const r = document.createRange();
                        r.setStart(parent, Math.min(idx, parent.childNodes.length));
                        r.setEnd(parent, Math.min(idx, parent.childNodes.length));
                        sel.removeAllRanges();
                        sel.addRange(r);
                        handleInput();
                    }
                } else if (key === 'Delete') {
                    const r = document.createRange();
                    r.setStartAfter(linkContainingCursor);
                    r.setEndAfter(linkContainingCursor);
                    sel.removeAllRanges();
                    sel.addRange(r);
                }
                return;
            }

            const isInsideOrAdjacentToLink = (node) => !!getLinkToSkip(node);

            if (key === 'ArrowRight') {
                const next = getNextNode(range.endContainer, range.endOffset);
                const link = next && getLinkToSkip(next);
                if (link) {
                    e.preventDefault();
                    const r = document.createRange();
                    r.setStartAfter(link);
                    r.setEndAfter(link);
                    sel.removeAllRanges();
                    sel.addRange(r);
                }
                return;
            }

            if (key === 'ArrowLeft') {
                const prev = getPreviousNode(range.startContainer, range.startOffset);
                const link = prev && getLinkToSkip(prev);
                if (link) {
                    e.preventDefault();
                    const r = document.createRange();
                    r.setStartBefore(link);
                    r.setEndBefore(link);
                    sel.removeAllRanges();
                    sel.addRange(r);
                }
                return;
            }

            if (key === 'Backspace') {
                const startContainer = range.startContainer;
                const startOffset = range.startOffset;
                let nodeToCheck = null;
                if (startContainer.nodeType === Node.TEXT_NODE && startOffset > 0) {
                    nodeToCheck = startContainer;
                } else if (startContainer.nodeType === Node.TEXT_NODE && startOffset === 0) {
                    nodeToCheck = startContainer.previousSibling ?? startContainer.parentElement?.previousSibling ?? startContainer.parentElement;
                } else if (startContainer.nodeType === Node.ELEMENT_NODE) {
                    nodeToCheck = startOffset > 0 ? startContainer.childNodes[startOffset - 1] : startContainer.previousSibling;
                }
                if (!nodeToCheck || !isInsideOrAdjacentToLink(nodeToCheck)) return;
                e.preventDefault();
                const link = nodeToCheck.nodeType === Node.ELEMENT_NODE && nodeToCheck.tagName === 'A'
                    ? nodeToCheck
                    : nodeToCheck.parentElement?.closest?.('a');
                if (link && el.contains(link)) {
                    const href = link.getAttribute('href') || '';
                    const attachmentMatch = href.match(/^#attachment-(\d+)$/);
                    const removedAttachmentIndex = link.hasAttribute('data-attachment') && attachmentMatch ? parseInt(attachmentMatch[1], 10) : -1;
                    if (removedAttachmentIndex >= 0 && onAttachmentRemoved) onAttachmentRemoved(removedAttachmentIndex);
                    const isMentionLink = href.startsWith('#mention-user-') || href.startsWith('#mention-milestone-') || href.startsWith('#mention-sprint-');
                    if (removedAttachmentIndex < 0 && !isMentionLink && typeof onLinkRemoved === 'function' && href && !href.startsWith('#')) {
                        onLinkRemoved(href);
                    }
                    const parent = link.parentNode;
                    if (parent) {
                        const idx = Array.from(parent.childNodes).indexOf(link);
                        link.remove();
                        if (removedAttachmentIndex >= 0) {
                            el.querySelectorAll('a[href^="#attachment-"]').forEach((a) => {
                                const m = (a.getAttribute('href') || '').match(/^#attachment-(\d+)$/);
                                if (m) {
                                    const n = parseInt(m[1], 10);
                                    if (n > removedAttachmentIndex) a.setAttribute('href', `#attachment-${n - 1}`);
                                }
                            });
                        }
                        const r = document.createRange();
                        r.setStart(parent, Math.min(idx, parent.childNodes.length));
                        r.setEnd(parent, Math.min(idx, parent.childNodes.length));
                        sel.removeAllRanges();
                        sel.addRange(r);
                        handleInput();
                    }
                }
                return;
            }

            if (key === 'Delete') {
                const endContainer = range.endContainer;
                const endOffset = range.endOffset;
                let nodeToCheck = null;
                if (endContainer.nodeType === Node.TEXT_NODE && endOffset < (endContainer.textContent?.length ?? 0)) {
                    nodeToCheck = endContainer;
                } else if (endContainer.nodeType === Node.TEXT_NODE && endOffset === (endContainer.textContent?.length ?? 0)) {
                    nodeToCheck = endContainer.nextSibling ?? endContainer.parentElement?.nextSibling ?? endContainer.parentElement;
                } else if (endContainer.nodeType === Node.ELEMENT_NODE) {
                    const len = endContainer.childNodes?.length ?? 0;
                    nodeToCheck = endOffset < len ? endContainer.childNodes[endOffset] : endContainer.nextSibling;
                }
                if (nodeToCheck && isInsideOrAdjacentToLink(nodeToCheck)) e.preventDefault();
                return;
            }
        },
        [
            handleInput,
            isMention,
            onAttachmentRemoved,
            mentionOpen,
            filteredMentionItems,
            mentionHighlightIndex,
            insertMention,
            closeMentionDropdown,
        ],
    );

    const showAttachResolved = isAttachment && showAttachButton;

    const mentionDropdownNode =
        typeof document !== 'undefined' &&
        mentionOpen &&
        filteredMentionItems.length > 0 &&
        mentionPosition &&
        createPortal(
            <MentionDropdown
                ref={mentionDropdownRef}
                style={{
                    top: mentionPosition.top,
                    left: mentionPosition.left,
                }}
            >
                {(() => {
                    let isFirstSectionHeader = true;
                    return (['user', 'milestone', 'sprint', 'tag']).map((sectionType) => {
                        const sectionItems = filteredMentionItems.filter((it) => it.type === sectionType);
                        if (sectionItems.length === 0) return null;
                        const sectionLabel =
                            sectionType === 'user' ? 'Users' : sectionType === 'milestone' ? 'Milestones' : sectionType === 'sprint' ? 'Sprints' : 'Tags';
                        const firstIndex = filteredMentionItems.indexOf(sectionItems[0]);
                        const headerFirst = isFirstSectionHeader;
                        isFirstSectionHeader = false;
                        return (
                            <div key={sectionType}>
                                <MentionSectionHeader $first={headerFirst}>{sectionLabel}</MentionSectionHeader>
                                {sectionItems.map((item, idx) => {
                                    const flatIndex = firstIndex + idx;
                                    return (
                                        <MentionItem
                                            key={`${item.type}-${item.id}`}
                                            type="button"
                                            $active={mentionKeyboardNav && flatIndex === mentionHighlightIndex}
                                            onMouseDown={(ev) => {
                                                ev.preventDefault();
                                                insertMention(item);
                                            }}
                                        >
                                            {item.type === 'user' ? `@${item.label}` : item.label}
                                        </MentionItem>
                                    );
                                })}
                            </div>
                        );
                    });
                })()}
            </MentionDropdown>,
            document.body,
        );

    return (
        <>
            <Wrapper {...rest} $submissionNeutral={submissionNeutralLight}>
                <Toolbar $submissionNeutral={submissionNeutralLight}>
                    <ToolbarBtn type="button" $active={formatState.bold} onMouseDown={handleToolbar('bold')} title="Bold">
                        <BoldOutlined />
                    </ToolbarBtn>
                    <ToolbarBtn
                        type="button"
                        $active={formatState.italic}
                        onMouseDown={handleToolbar('italic')}
                        title="Italic"
                    >
                        <ItalicOutlined />
                    </ToolbarBtn>
                    <ToolbarBtn
                        type="button"
                        $active={formatState.underline}
                        onMouseDown={handleToolbar('underline')}
                        title="Underline"
                    >
                        <UnderlineOutlined />
                    </ToolbarBtn>
                    <ToolbarBtn
                        type="button"
                        $active={formatState.strikeThrough}
                        onMouseDown={handleToolbar('strikeThrough')}
                        title="Strikethrough"
                    >
                        <StrikethroughOutlined />
                    </ToolbarBtn>
                    <ToolbarBtn
                        type="button"
                        $active={formatState.insertUnorderedList}
                        onMouseDown={handleToolbar('insertUnorderedList')}
                        title="Bullet list"
                    >
                        <UnorderedListOutlined />
                    </ToolbarBtn>
                    <ToolbarBtn
                        type="button"
                        $active={formatState.insertOrderedList}
                        onMouseDown={handleToolbar('insertOrderedList')}
                        title="Numbered list"
                    >
                        <OrderedListOutlined />
                    </ToolbarBtn>
                    {isLink && (
                        <ToolbarBtn type="button" onMouseDown={handleLink} title="Insert link">
                            <LinkOutlined />
                        </ToolbarBtn>
                    )}
                    {showAttachResolved && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                tabIndex={-1}
                                accept="image/*,.pdf,.doc,.docx,*/*"
                                multiple
                                style={{ display: 'none' }}
                                onChange={handleAttachFileChange}
                            />
                            <ToolbarBtn type="button" onMouseDown={(e) => { e.preventDefault(); handleAttachClick(); }} title="Attach file">
                                <PaperClipOutlined />
                            </ToolbarBtn>
                        </>
                    )}
                    {showEmojiPicker && (
                        <Popover
                            trigger="click"
                            content={
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: 240, padding: 4 }}>
                                    {EMOJI_LIST.map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            style={{
                                                width: 32,
                                                height: 32,
                                                border: 'none',
                                                background: 'transparent',
                                                cursor: 'pointer',
                                                fontSize: 18,
                                                borderRadius: 4,
                                                padding: 0,
                                            }}
                                            onClick={() => insertEmoji(emoji)}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            }
                            title="Insert emoji"
                        >
                            <ToolbarBtn type="button" title="Insert emoji">
                                <SmileOutlined />
                            </ToolbarBtn>
                        </Popover>
                    )}
                </Toolbar>
                <div style={{ position: 'relative' }}>
                    <EditorArea
                        ref={elRef}
                        tabIndex={disabled ? -1 : 0}
                        $submissionNeutral={submissionNeutralLight}
                        contentEditable={!disabled}
                        suppressContentEditableWarning
                        onInput={handleInput}
                        onPaste={handlePaste}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onKeyDown={handleEditorKeyDown}
                        onKeyUp={updateFormatState}
                        onMouseUp={updateFormatState}
                        onBlur={() => {
                            if (mentionOpen) closeMentionDropdown();
                        }}
                        onClick={handleEditorClick}
                        data-placeholder={placeholder}
                        style={{
                            ...(isDraggingOver && !disabled && { outline: '2px dashed var(--ant-color-primary, #7c3aed)' }),
                            ...(disabled && { opacity: 0.6, pointerEvents: 'none' }),
                        }}
                    />
                </div>
            </Wrapper>
            {mentionDropdownNode}
        </>
    );
}

export default RichTextEditor;
